import { type ReactNode } from 'react';
import { useMemo } from 'react';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Tooltip } from 'react-tooltip';
import { motion } from 'framer-motion';
import { rgba } from 'polished';
import styled, { css, ThemeProvider } from 'styled-components';

import { Button, Heading, Text } from '@/components/design-system';
import { useHasMounted } from '@/hooks/useHasMounted';
import { SIGNUP_LINK } from '@/utils';
import { type ThemeDefinition } from '@/utils/theme/definition';
import { GlobalAnimations } from '@/utils/theme/globalAnimations';
import { GlobalStyle } from '@/utils/theme/globalStyles';
import { DARK_THEME, LIGHT_THEME } from '@/utils/theme/themes';

import { Nav, type NavProps } from './navigation/Nav';
import { NavMinimal } from './navigation/NavMinimal';
import { Footer } from './Footer';
import { GRID_SIZE, PaperGrid } from './PaperGrid';
import { SEO, type SEOProps } from './SEO';

const Wrapper = styled.div<{
  $isometricGradientBackground: boolean;
  $noiseBackground: boolean;
  $showLines: boolean;
}>`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  overflow: hidden;

  ${({ $showLines }) =>
    $showLines &&
    css`
      background-size: 271px 270px;
      background-position: center;
      background-image: linear-gradient(
        to left,
        transparent 135px,
        ${({ theme }) => theme.borderColor.MUTED} 135px,
        transparent 136px
      );
    `};

  ${({ $isometricGradientBackground, theme }) =>
    $isometricGradientBackground &&
    css`
      background-image: linear-gradient(
        180deg,
        ${rgba(theme.borderColor.DEFAULT, 0.1)} 5%,
        ${rgba(theme.backgroundColor, 1)} 10%
      );

      &::before {
        content: '';
        display: block;
        pointer-events: none;
        position: absolute;
        top: 0;
        opacity: 0.5;
        left: 0;
        right: 0;
        height: 1200px;
        background-size: 150px auto;
        background-image: url('/images/make-your-data-team-unstoppable/isometric-pattern.svg');
      }
    `};

  ${({ $noiseBackground }) =>
    $noiseBackground &&
    css`
      padding: ${GRID_SIZE}px;

      &::before {
        content: '';
        display: block;
        pointer-events: none;
        position: absolute;
        top: 0;
        opacity: 0.05;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: url('/images/bkg-noise.webp');
        background-repeat: repeat;
        background-size: 200px 200px;
      }

      @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
        padding: 0;
      }
    `};
`;

const Header = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 60px;
  padding: 0 40px;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

const Left = styled.div<{ $fullWidth: boolean }>`
  max-width: 50%;

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      max-width: 75%;
    `}

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    max-width: none;
  }
`;

const Right = styled.div`
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    margin-left: 0;
    width: 100%;
  }
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 20px;
  text-indent: -4px;

  span {
    display: block;
  }
`;

interface PageProps {
  children: ReactNode;
  headerProps?: {
    description: ReactNode;
    rightElement?: JSX.Element;
    title: string;
  };
  isometricGradientBackground?: boolean;
  navMinimalCustomAction?: ReactNode;
  navProps?: NavProps;
  navType?: 'default' | 'minimal' | 'minimal--get-started' | 'none';
  noiseBackground?: boolean;
  seoProps: SEOProps;
  showFooter?: boolean;
  showLines?: boolean;
  theme?: 'dark' | 'light' | ThemeDefinition;
}

export function Fonts() {
  return (
    <Helmet>
      <link
        as="font"
        crossOrigin="anonymous"
        href="/fonts/formula/PP-Formula-SemiExtendedBold.woff2"
        rel="preload"
        type="font/woff2"
      />
      <link
        as="font"
        crossOrigin="anonymous"
        href="/fonts/cinetype/GT-Cinetype-Light.woff2"
        rel="preload"
        type="font/woff2"
      />
      <link
        as="image"
        fetchPriority="high"
        href="/images/bkg-noise.webp"
        rel="preload"
      />
      <link
        href="/styles/brand-refresh-fonts.css"
        rel="stylesheet"
        type="text/css"
      />
      <link
        href="https://fonts.googleapis.com/css?family=IBM+Plex+Sans:300,400,500,600,700|IBM+Plex+Mono:400,500,700|IBM+Plex+Serif:300,400,500,600,700&amp;display=swap"
        media="print"
        // @ts-expect-error this is a valid link attribute
        onload="this.media='all'"
        rel="stylesheet"
      />
    </Helmet>
  );
}

export const Page: React.ComponentType<PageProps> = React.memo(
  ({
    children,
    isometricGradientBackground = false,
    noiseBackground = true,
    navMinimalCustomAction,
    seoProps,
    navProps,
    theme = 'dark',
    navType = 'default',
    showFooter = true,
    showLines = true,
    headerProps
  }) => {
    const hasMounted = useHasMounted();

    const navigation = useMemo(() => {
      switch (navType) {
        case 'default':
          return <Nav {...navProps} />;
        case 'minimal':
          return <NavMinimal customAction={navMinimalCustomAction} />;
        case 'minimal--get-started':
          return (
            <NavMinimal
              customAction={
                <Button
                  link={SIGNUP_LINK}
                  size="default"
                  stylez="loud"
                  text="Get started"
                />
              }
            />
          );
        default:
          return null;
      }
    }, [navType, navProps, navMinimalCustomAction]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function instanceOfTheme(data: any): data is ThemeDefinition {
      return 'backgroundColor' in data;
    }

    const thisTheme = useMemo(() => {
      if (typeof theme !== 'string' && instanceOfTheme(theme)) {
        return theme;
      }

      switch (theme) {
        case 'dark':
          return DARK_THEME;
        case 'light':
          return LIGHT_THEME;
        default:
          return DARK_THEME;
      }
    }, [theme]);

    return (
      <ThemeProvider theme={thisTheme}>
        <Fonts />
        <Wrapper
          $isometricGradientBackground={isometricGradientBackground}
          $noiseBackground={noiseBackground}
          $showLines={showLines}>
          <SEO {...seoProps} />
          <GlobalStyle />
          <GlobalAnimations />
          {noiseBackground && <PaperGrid />}
          {hasMounted && (
            <Tooltip className="toolTip" clickable={true} id="tooltip" />
          )}
          {navigation}
          {headerProps && (
            <Header className="hero-fade-in">
              <Left $fullWidth={headerProps.rightElement === undefined}>
                <StyledHeading renderAs="h1">{headerProps.title}</StyledHeading>
                <Text size="large">{headerProps.description}</Text>
              </Left>
              {headerProps.rightElement && (
                <Right>{headerProps.rightElement}</Right>
              )}
            </Header>
          )}
          {children}
          {showFooter && <Footer />}
        </Wrapper>
      </ThemeProvider>
    );
  }
);
